// Newsletter.js

import React, { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, X } from 'lucide-react';

// Redux state management (replace with real Redux in your project)
const initialState = {
  subscribers: [],
  isLoading: false,
  error: null,
  successMessage: '',
};

const SUBSCRIBE_START = 'newsletter/subscribeStart';
const SUBSCRIBE_SUCCESS = 'newsletter/subscribeSuccess';
const SUBSCRIBE_ERROR = 'newsletter/subscribeError';
const CLEAR_MESSAGES = 'newsletter/clearMessages';

const newsletterReducer = (state = initialState, action) => {
  switch (action.type) {
    case SUBSCRIBE_START:
      return { ...state, isLoading: true, error: null, successMessage: '' };
    case SUBSCRIBE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        subscribers: [...state.subscribers, action.payload],
        successMessage: 'Successfully subscribed to our newsletter!',
        error: null,
      };
    case SUBSCRIBE_ERROR:
      return { ...state, isLoading: false, error: action.payload, successMessage: '' };
    case CLEAR_MESSAGES:
      return { ...state, error: null, successMessage: '' };
    default:
      return state;
  }
};

// Utility functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isGmailAddress = (email) => {
  return email.toLowerCase().includes('@gmail.com');
};

// Newsletter Component
const Newsletter = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState(initialState);
  // NEW: State to track which input is focused
  const [focusedInput, setFocusedInput] = useState(null);

  const { isLoading, error, successMessage, subscribers } = state;

  const dispatch = (action) => {
    setState((prevState) => newsletterReducer(prevState, action));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email.trim()) {
      dispatch({ type: SUBSCRIBE_ERROR, payload: 'Email is required' });
      return;
    }

    if (!name.trim()) {
      dispatch({ type: SUBSCRIBE_ERROR, payload: 'Name is required' });
      return;
    }

    dispatch({ type: SUBSCRIBE_START });

    // Simulate API call
    setTimeout(() => {
      try {
        if (!validateEmail(email)) {
          throw new Error('Please enter a valid email address');
        }

        if (subscribers.some((sub) => sub.email === email)) {
          throw new Error('This email is already subscribed');
        }

        const subscriber = {
          id: Date.now(),
          email,
          name,
          subscribedAt: new Date().toISOString(),
          isGmail: isGmailAddress(email),
          verified: false,
        };

        dispatch({ type: SUBSCRIBE_SUCCESS, payload: subscriber });
        setEmail('');
        setName('');

        // Auto-close after success
        setTimeout(() => {
          dispatch({ type: CLEAR_MESSAGES });
          onClose();
        }, 2000);
      } catch (err) {
        dispatch({ type: SUBSCRIBE_ERROR, payload: err.message });

        setTimeout(() => {
          dispatch({ type: CLEAR_MESSAGES });
        }, 5000);
      }
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <Mail size={24} style={styles.headerIcon} />
            <h3 style={styles.title}>Newsletter</h3>
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} color="white" />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          <div style={styles.welcomeSection}>
            <h4 style={styles.welcomeTitle}>Stay in the loop!</h4>
            <p style={styles.subtitle}>
              Get exclusive updates, news, and special offers delivered straight to your inbox.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <input
                type="text"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                // NEW: Add onFocus and onBlur handlers
                onFocus={() => setFocusedInput('name')}
                onBlur={() => setFocusedInput(null)}
                style={{
                  ...styles.input,
                  // NEW: Conditionally apply focus style
                  ...(focusedInput === 'name' ? styles.inputFocused : {}),
                }}
                disabled={isLoading}
              />
            </div>

            <div style={styles.inputGroup}>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                // NEW: Add onFocus and onBlur handlers
                onFocus={() => setFocusedInput('email')}
                onBlur={() => setFocusedInput(null)}
                style={{
                  ...styles.input,
                  // NEW: Conditionally apply focus style
                  ...(focusedInput === 'email' ? styles.inputFocused : {}),
                }}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(isLoading ? styles.submitButtonDisabled : {}),
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <div style={styles.spinner}></div>
              ) : (
                <>
                  <Send size={16} />
                  Subscribe Now
                </>
              )}
            </button>
          </form>

          {/* Status Messages */}
          {error && (
            <div style={styles.errorMessage}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div style={styles.successMessage}>
              <CheckCircle size={16} />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Features List */}
          <div style={styles.features}>
            <div style={styles.feature}>
              <CheckCircle size={14} style={styles.featureIcon} />
              <span>Weekly industry insights</span>
            </div>
            <div style={styles.feature}>
              <CheckCircle size={14} style={styles.featureIcon} />
              <span>Exclusive content & offers</span>
            </div>
            <div style={styles.feature}>
              <CheckCircle size={14} style={styles.featureIcon} />
              <span>No spam, unsubscribe anytime</span>
            </div>
          </div>

          {/* Stats */}
          <div style={styles.stats}>
            <div style={styles.stat}>
              <strong>{subscribers.length}</strong>
              <span>Subscribers</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles for Newsletter Component
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    zIndex: 1000,
    padding: '20px',
  },
  popup: {
    backgroundColor: 'white',
    width: '380px',
    maxHeight: '90vh',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    animation: 'slideInRight 0.3s ease-out',
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative', // Support absolute positioning of close button
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  },
  title: {
    margin: 0,
    fontSize: '1.4em',
    fontWeight: 'bold',
  },
  closeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'white',
    transition: 'all 0.3s ease',
    position: 'absolute', // Position in top-right corner
    top: '20px',
    right: '15px',
    zIndex: 10, // Ensure it’s above other elements
    padding: 0, // Remove default padding
  },
  content: {
    padding: '24px',
    maxHeight: 'calc(90vh - 80px)',
    overflowY: 'auto',
  },
  welcomeSection: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  welcomeTitle: {
    margin: '0 0 8px 0',
    fontSize: '1.2em',
    color: '#2d3748',
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.5',
    margin: 0,
  },
  form: {
    marginBottom: '20px',
  },
  inputGroup: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '14px',
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit',
  },
  // NEW: Style for focused input
  inputFocused: {
    border: '1px solid #667eea',
    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
  },
  submitButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  submitButtonDisabled: {
    backgroundColor: '#cbd5e0',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#fed7d7',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
    border: '1px solid rgba(197, 48, 48, 0.2)',
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#c6f6d5',
    color: '#2f855a',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
    border: '1px solid rgba(47, 133, 90, 0.2)',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#f7fafc',
    borderRadius: '12px',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#4a5568',
  },
  featureIcon: {
    color: '#48bb78',
  },
  stats: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '16px',
    backgroundColor: '#edf2f7',
    borderRadius: '12px',
    marginTop: '16px',
  },
  stat: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '12px',
    color: '#718096',
  },
};

// CSS Animations
const newsletterCSS = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'newsletter-styles';
  styleSheet.textContent = newsletterCSS;
  if (!document.getElementById('newsletter-styles')) {
    document.head.appendChild(styleSheet);
  }
}

export default Newsletter;

// Export Redux parts for integration
export {
  newsletterReducer,
  SUBSCRIBE_START,
  SUBSCRIBE_SUCCESS,
  SUBSCRIBE_ERROR,
  CLEAR_MESSAGES,
};